import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './LegalPage.css';

const PrivacyPolicy: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Privacy Policy — FitMind AI';
  }, []);

  const lastUpdated = 'April 8, 2025';

  return (
    <div className="legal-page">
      <header className="legal-header">
        <Link to="/" className="legal-logo">
          <img src="/logo.png" alt="FitMind AI" />
        </Link>
      </header>

      <main className="legal-main container">
        <div className="legal-content">
          <div className="legal-title-block">
            <span className="legal-tag">Legal</span>
            <h1>Privacy Policy</h1>
            <p className="legal-meta">Last updated: {lastUpdated}</p>
          </div>

          <p className="legal-intro">
            FitMind AI ("we", "our", "us") is committed to protecting your privacy. This Privacy Policy
            explains what personal data we collect, how we use it, and your rights in relation to it.
            By using the FitMind AI mobile application, you agree to the practices described in this policy.
          </p>

          <Section title="1. Who We Are">
            <p>
              FitMind AI is an AI-powered fitness coaching application available on Android via Google Play.
              If you have any questions about this policy, you can contact us at{' '}
              <a href="mailto:fitmindai@gmail.com">fitmindai@gmail.com</a>.
            </p>
          </Section>

          <Section title="2. Data We Collect">
            <p>We collect the following categories of personal data:</p>
            <SubSection title="2.1 Account Data">
              <ul>
                <li>Email address and display name (provided during registration or Google Sign-In)</li>
                <li>Profile photo (optional, from your Google account)</li>
              </ul>
            </SubSection>
            <SubSection title="2.2 Onboarding & Profile Data">
              <ul>
                <li>Fitness goal (e.g. build muscle, lose fat, improve fitness)</li>
                <li>Training experience level</li>
                <li>Available training equipment and environment (gym / home)</li>
                <li>Diet type and food preferences</li>
                <li>Training schedule and session frequency</li>
                <li>Gender, age, height and weight</li>
                <li>Job type and stress level (used to calibrate recovery recommendations)</li>
              </ul>
            </SubSection>
            <SubSection title="2.3 Training & Activity Data">
              <ul>
                <li>Workout sessions, sets, reps and weights logged</li>
                <li>Pre-session check-in data (energy level, mood, sleep quality)</li>
                <li>Post-session feedback and AI analysis results</li>
                <li>Exercise history and personal records</li>
              </ul>
            </SubSection>
            <SubSection title="2.4 Body & Health Data">
              <ul>
                <li>Body weight entries over time</li>
                <li>Body measurements (e.g. chest, waist, hips, arms) — if provided</li>
                <li>Progress photos — if uploaded by you</li>
              </ul>
            </SubSection>
            <SubSection title="2.5 Nutrition Data">
              <ul>
                <li>Daily food diary entries (foods, portions, calorie and macro tracking)</li>
              </ul>
            </SubSection>
            <SubSection title="2.6 Payment Data">
              <p>
                We do not store your payment card details. Subscription payments are processed
                by Google Play and managed by RevenueCat. We only receive anonymised subscription
                status information (active, expired, trial).
              </p>
            </SubSection>
            <SubSection title="2.7 Usage & Technical Data">
              <ul>
                <li>App usage patterns and feature interactions</li>
                <li>Device type and operating system version</li>
                <li>Crash reports and error logs</li>
                <li>Push notification tokens (for workout reminders)</li>
              </ul>
            </SubSection>
          </Section>

          <Section title="3. How We Use Your Data">
            <p>We use your personal data to:</p>
            <ul>
              <li>Create and manage your account</li>
              <li>Generate your personalised AI training plan and nutrition guide</li>
              <li>Provide AI Coach responses tailored to your profile and history</li>
              <li>Suggest weights and intensities based on your previous sessions</li>
              <li>Recalibrate and adapt your plan monthly based on your progress</li>
              <li>Display your progress charts, body measurements and progress photo timeline</li>
              <li>Send push notifications for workout reminders (only if you enable them)</li>
              <li>Manage your subscription and access permissions</li>
              <li>Improve app performance and fix bugs</li>
            </ul>
            <p>
              <strong>We never use your health or body data for advertising purposes.</strong>
            </p>
          </Section>

          <Section title="4. Third-Party Services">
            <p>FitMind AI uses the following third-party services that may process your data:</p>
            <table className="legal-table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Purpose</th>
                  <th>Privacy Policy</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Firebase (Google)</td>
                  <td>Authentication, database, file storage, push notifications</td>
                  <td><a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer">View</a></td>
                </tr>
                <tr>
                  <td>OpenAI</td>
                  <td>AI coaching responses and plan generation</td>
                  <td><a href="https://openai.com/privacy" target="_blank" rel="noopener noreferrer">View</a></td>
                </tr>
                <tr>
                  <td>RevenueCat</td>
                  <td>Subscription management</td>
                  <td><a href="https://www.revenuecat.com/privacy" target="_blank" rel="noopener noreferrer">View</a></td>
                </tr>
                <tr>
                  <td>Google Sign-In</td>
                  <td>Authentication</td>
                  <td><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">View</a></td>
                </tr>
              </tbody>
            </table>
            <p>
              When you use the AI Coach, your messages and relevant profile context are sent to
              OpenAI's API to generate responses. We do not share identifiable personal information
              such as your name or email with OpenAI.
            </p>
          </Section>

          <Section title="5. Data Storage & Security">
            <p>
              Your data is stored in Google Firebase's cloud infrastructure (Firestore and Firebase Storage),
              which is hosted in secure Google data centres. We use Firebase's built-in security rules to
              ensure that each user can only access their own data.
            </p>
            <p>
              Progress photos you upload are stored in Firebase Storage and are private — accessible
              only to your account. We apply reasonable technical and organisational measures to protect
              your personal data against unauthorised access, loss or misuse.
            </p>
          </Section>

          <Section title="6. Data Retention">
            <p>
              We retain your personal data for as long as your account is active. If you delete your account,
              we will delete your personal data within 30 days, except where we are required by law to retain it longer.
            </p>
            <p>
              You can request deletion of your account and all associated data at any time by emailing{' '}
              <a href="mailto:fitmindai@gmail.com">fitmindai@gmail.com</a>.
            </p>
          </Section>

          <Section title="7. Your Rights">
            <p>Depending on your location, you may have the following rights regarding your personal data:</p>
            <ul>
              <li><strong>Access</strong> — request a copy of the data we hold about you</li>
              <li><strong>Correction</strong> — request that we correct inaccurate data</li>
              <li><strong>Deletion</strong> — request that we delete your data ("right to be forgotten")</li>
              <li><strong>Portability</strong> — request your data in a portable format</li>
              <li><strong>Objection</strong> — object to certain types of processing</li>
            </ul>
            <p>
              To exercise any of these rights, contact us at{' '}
              <a href="mailto:fitmindai@gmail.com">fitmindai@gmail.com</a>.
              We will respond within 30 days.
            </p>
          </Section>

          <Section title="8. Children's Privacy">
            <p>
              FitMind AI is not directed at children under the age of 13. We do not knowingly collect
              personal data from children under 13. If you believe a child has provided us with personal
              data, please contact us and we will delete it promptly.
            </p>
          </Section>

          <Section title="9. Push Notifications">
            <p>
              If you enable push notifications, we may send you workout reminders and motivational messages.
              You can disable push notifications at any time through your device's notification settings.
            </p>
          </Section>

          <Section title="10. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. When we do, we will update the
              "Last updated" date at the top of this page and notify you via the app if the changes are significant.
              Your continued use of FitMind AI after changes are posted constitutes your acceptance of the updated policy.
            </p>
          </Section>

          <Section title="11. Contact Us">
            <p>
              If you have any questions or concerns about this Privacy Policy, please contact us:
            </p>
            <div className="legal-contact-box">
              <div><strong>FitMind AI</strong></div>
              <div>Email: <a href="mailto:fitmindai@gmail.com">fitmindai@gmail.com</a></div>
            </div>
          </Section>
        </div>
      </main>

      <footer className="legal-footer">
        <div className="container">
          <Link to="/">← Back to FitMind AI</Link>
          <span>·</span>
          <Link to="/terms">Terms of Service</Link>
          <span>·</span>
          <span>© {new Date().getFullYear()} FitMind AI</span>
        </div>
      </footer>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="legal-section">
    <h2>{title}</h2>
    {children}
  </section>
);

const SubSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="legal-subsection">
    <h3>{title}</h3>
    {children}
  </div>
);

export default PrivacyPolicy;
